/**
 * Wizytówki Google - Panel Admina
 * P0: Lista zgłoszeń, statusy, checklisty
 * 
 * STATUSY:
 * - new: NOWE ZGŁOSZENIE
 * - data_verified: DANE UZUPEŁNIONE
 * - access_granted: DOSTĘP NADANY
 * - in_progress: W REALIZACJI
 * - ready: GOTOWA NA ZAPYTANIA
 * - in_care: W OPIECE
 * - alert: ALERT
 * 
 * BRANDING: Biel #FFFFFF, Czerń #0B0B0B, Magenta #E6007A
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { toast } from 'sonner';
import { 
  Check, Loader2, ChevronRight, Filter, RefreshCw,
  Phone, Mail, Globe, MapPinned, Clock, FileText, AlertTriangle, Trash2,
  ExternalLink, BookOpen, Facebook, Instagram, HelpCircle, CheckSquare
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import WizytowkiIcon from '@/components/icons/WizytowkiIcon';

import { API_BASE as API } from '@/utils/api';

const colors = {
  white: '#FFFFFF',
  black: '#0B0B0B',
  magenta: '#E6007A'
};

const STATUS_CONFIG = {
  new: { label: 'NOWE ZGŁOSZENIE', color: 'bg-blue-100 text-blue-800' },
  data_verified: { label: 'DANE UZUPEŁNIONE', color: 'bg-yellow-100 text-yellow-800' },
  access_granted: { label: 'DOSTĘP NADANY', color: 'bg-purple-100 text-purple-800' },
  in_progress: { label: 'W REALIZACJI', color: 'bg-orange-100 text-orange-800' },
  ready: { label: 'GOTOWA NA ZAPYTANIA', color: 'bg-green-100 text-green-800' },
  in_care: { label: 'W OPIECE', color: 'bg-teal-100 text-teal-800' },
  alert: { label: 'ALERT', color: 'bg-red-100 text-red-800' }
};

const CHECKLIST_ITEMS = [
  { id: 'survey_complete', label: 'Ankieta kompletna' },
  { id: 'contact_valid', label: 'Kontakt poprawny' },
  { id: 'manager_access_confirmed', label: 'Dostęp menedżera potwierdzony' },
  { id: 'photos_added', label: 'Zdjęcia dodane' },
  { id: 'google_visibility_verified', label: 'Widoczność sprawdzona w Google' }
];

export default function WizytowkiAdmin() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions'); // 'submissions' | 'leads'
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  // Fetch leads
  const fetchLeads = async () => {
    if (!token) return;
    setLeadsLoading(true);
    
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads);
      }
    } catch (err) {
      console.error('Leads error:', err);
    } finally {
      setLeadsLoading(false);
    }
  };
  
  // Delete lead
  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Usunąć ten email?')) return;
    
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setLeads(prev => prev.filter(l => l.id !== leadId));
        toast.success('Usunięto');
      }
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };
  
  // Fetch submissions
  const fetchSubmissions = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const url = filterStatus 
        ? `${API}/api/wizytowki/admin/submissions?status=${filterStatus}`
        : `${API}/api/wizytowki/admin/submissions`;
        
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions);
      } else if (res.status === 403) {
        toast.error('Brak dostępu. Tylko dla administratorów.');
      }
    } catch (err) {
      toast.error('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch stats
  const fetchStats = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      console.error('Stats error:', err);
    }
  };
  
  useEffect(() => {
    fetchSubmissions();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterStatus]);
  
  // Update checklist
  const handleChecklistUpdate = async (submissionId, checklistItem, checked) => {
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/submissions/${submissionId}/checklist`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ checklist_item: checklistItem, checked })
      });
      
      if (res.ok) {
        // Update local state
        setSubmissions(prev => prev.map(s => {
          if (s.id === submissionId) {
            return {
              ...s,
              checklist: { ...s.checklist, [checklistItem]: checked }
            };
          }
          return s;
        }));
        
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(prev => ({
            ...prev,
            checklist: { ...prev.checklist, [checklistItem]: checked }
          }));
        }
        
        toast.success('Checklist zaktualizowany');
      }
    } catch (err) {
      toast.error('Błąd aktualizacji');
    }
  };
  
  // Update status
  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/submissions/${submissionId}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_status: newStatus })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Update local state
        setSubmissions(prev => prev.map(s => {
          if (s.id === submissionId) {
            return { ...s, status: newStatus, status_label: data.new_status_label };
          }
          return s;
        }));
        
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(prev => ({
            ...prev,
            status: newStatus,
            status_label: data.new_status_label
          }));
        }
        
        toast.success(`Status zmieniony: ${data.new_status_label}`);
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Nie można zmienić statusu');
      }
    } catch (err) {
      toast.error('Błąd zmiany statusu');
    }
  };
  
  // Add note
  const handleAddNote = async (submissionId, content) => {
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/submissions/${submissionId}/notes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(prev => ({
            ...prev,
            notes: [...prev.notes, data.note]
          }));
        }
        
        toast.success('Notatka dodana');
      }
    } catch (err) {
      toast.error('Błąd dodawania notatki');
    }
  };
  
  // Delete submission
  const handleDelete = async (submissionId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zgłoszenie?')) return;
    
    try {
      const res = await fetch(`${API}/api/wizytowki/admin/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSubmissions(prev => prev.filter(s => s.id !== submissionId));
        setDetailsOpen(false);
        setSelectedSubmission(null);
        fetchStats();
        toast.success('Zgłoszenie usunięte');
      } else {
        toast.error('Nie udało się usunąć');
      }
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <PanelLayout currentPage="Admin Wizytówki">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <WizytowkiIcon size={48} />
            <div>
              <h1 className="text-2xl font-bold" style={{ color: colors.black }}>
                Wizytówki Google
              </h1>
              <p className="text-gray-500 text-sm">Panel administratora</p>
            </div>
          </div>
          
          <button
            onClick={() => { fetchSubmissions(); fetchStats(); }}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Odśwież
          </button>
        </div>
        
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
              <button
                key={status}
                onClick={() => setFilterStatus(filterStatus === status ? '' : status)}
                className={`p-4 rounded-xl text-center transition-all ${
                  filterStatus === status ? 'ring-2 ring-offset-2' : ''
                }`}
                style={filterStatus === status ? { ringColor: colors.magenta } : {}}
              >
                <p className="text-2xl font-bold">{stats.by_status[status] || 0}</p>
                <p className="text-xs text-gray-500">{config.label}</p>
              </button>
            ))}
          </div>
        )}
        
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Filtr:</span>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none"
          >
            <option value="">Wszystkie</option>
            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
              <option key={status} value={status}>{config.label}</option>
            ))}
          </select>
          
          {filterStatus && (
            <button
              onClick={() => setFilterStatus('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Wyczyść
            </button>
          )}
        </div>
        
        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            Brak zgłoszeń
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map((sub) => {
              const statusConfig = STATUS_CONFIG[sub.status] || STATUS_CONFIG.new;
              
              return (
                <div
                  key={sub.id}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => { setSelectedSubmission(sub); setDetailsOpen(true); }}
                  data-testid={`submission-${sub.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <h3 className="font-semibold text-lg">{sub.kennel_name}</h3>
                        <p className="text-gray-500 text-sm">{sub.city}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      <span className="text-sm text-gray-400">
                        {formatDate(sub.created_at)}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                  
                  {/* Quick info */}
                  <div className="flex items-center gap-6 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {sub.phone}
                    </span>
                    {sub.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {sub.email}
                      </span>
                    )}
                    <span className="flex items-center gap-1" style={{ color: colors.magenta }}>
                      {sub.service_type === 'subscription' ? 'Opieka' : 'Jednorazowo'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Details Modal */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>{selectedSubmission?.kennel_name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  STATUS_CONFIG[selectedSubmission?.status]?.color || ''
                }`}>
                  {selectedSubmission?.status_label}
                </span>
              </DialogTitle>
            </DialogHeader>
            
            {selectedSubmission && (
              <SubmissionDetails
                submission={selectedSubmission}
                onChecklistUpdate={handleChecklistUpdate}
                onStatusUpdate={handleStatusUpdate}
                onAddNote={handleAddNote}
                onDelete={handleDelete}
                formatDate={formatDate}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PanelLayout>
  );
}

// ==================== SOP COMPONENT ====================

function SOPInstructions() {
  const [sopChecklist, setSopChecklist] = useState({
    www_added: false,
    www_saved: false,
    www_verified: false,
    fb_added: false,
    ig_added: false,
    fallback_checked: false,
    fallback_note_added: false
  });
  
  const toggleSop = (key) => {
    setSopChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  return (
    <div className="space-y-4">
      {/* A) Dodaj stronę WWW */}
      <div className="bg-gradient-to-r from-pink-50 to-white border border-pink-100 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5" style={{ color: colors.magenta }} />
          <h4 className="font-semibold" style={{ color: colors.magenta }}>A) Dodaj stronę WWW do wizytówki</h4>
        </div>
        
        <div className="mb-4">
          <a 
            href="https://business.google.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            Otwórz Google Business Profile
          </a>
        </div>
        
        <ol className="text-sm text-gray-700 space-y-2 mb-4 list-decimal list-inside">
          <li>Zaloguj się na właściwe konto Google</li>
          <li>Wybierz wizytówkę (jeśli jest kilka firm/profili)</li>
          <li>Kliknij <strong>Edytuj profil</strong> (lub „Edit profile")</li>
          <li>Wejdź w zakładkę <strong>Kontakt</strong> (lub „Contact")</li>
          <li>Znajdź pole <strong>Strona internetowa / Website</strong></li>
          <li>Wklej pełny adres, np. <code className="bg-gray-100 px-1 rounded">https://twojahodowla.pl</code></li>
          <li>Kliknij <strong>Zapisz</strong> i sprawdź po chwili w panelu / Mapach</li>
        </ol>
        
        <div className="space-y-2 border-t border-pink-100 pt-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.www_added} 
              onChange={() => toggleSop('www_added')}
              className="w-4 h-4 rounded accent-pink-600"
            />
            <span className={sopChecklist.www_added ? 'text-green-700 font-medium' : ''}>
              ☐ WWW dodana w polu „Website"
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.www_saved} 
              onChange={() => toggleSop('www_saved')}
              className="w-4 h-4 rounded accent-pink-600"
            />
            <span className={sopChecklist.www_saved ? 'text-green-700 font-medium' : ''}>
              ☐ Zapisane
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.www_verified} 
              onChange={() => toggleSop('www_verified')}
              className="w-4 h-4 rounded accent-pink-600"
            />
            <span className={sopChecklist.www_verified ? 'text-green-700 font-medium' : ''}>
              ☐ Sprawdzone po zapisie (widoczne w panelu)
            </span>
          </label>
        </div>
      </div>
      
      {/* B) Dodatkowe linki */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold text-gray-700">B) Dodatkowe linki (opcjonalnie)</h4>
        </div>
        
        <p className="text-sm text-gray-500 mb-3">Jeśli klient ma social media i Google pozwala na dodanie:</p>
        
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.fb_added} 
              onChange={() => toggleSop('fb_added')}
              className="w-4 h-4 rounded"
            />
            <Facebook className="w-4 h-4 text-blue-600" />
            <span className={sopChecklist.fb_added ? 'text-green-700 font-medium' : ''}>
              FB dodany (jeśli jest pole)
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.ig_added} 
              onChange={() => toggleSop('ig_added')}
              className="w-4 h-4 rounded"
            />
            <Instagram className="w-4 h-4 text-pink-600" />
            <span className={sopChecklist.ig_added ? 'text-green-700 font-medium' : ''}>
              IG dodany (jeśli jest pole)
            </span>
          </label>
        </div>
      </div>
      
      {/* C) Fallback */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="w-5 h-5 text-yellow-600" />
          <h4 className="font-semibold text-yellow-800">C) Fallback - gdy nie widzisz pola WWW</h4>
        </div>
        
        <ul className="text-sm text-gray-700 space-y-1 mb-3 list-disc list-inside">
          <li>Upewnij się, że masz rolę <strong>Menedżer</strong> lub wyższą</li>
          <li>Spróbuj: Edytuj profil → przewiń do sekcji Kontakt</li>
          <li>Lub: Informacje o firmie (Google zmienia nazwy sekcji)</li>
          <li>Sprawdź czy to właściwy profil (czasem duplikat)</li>
          <li>Sprawdź czy wizytówka jest zweryfikowana</li>
        </ul>
        
        <div className="space-y-2 border-t border-yellow-200 pt-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.fallback_checked} 
              onChange={() => toggleSop('fallback_checked')}
              className="w-4 h-4 rounded accent-yellow-600"
            />
            <span className={sopChecklist.fallback_checked ? 'text-green-700 font-medium' : ''}>
              ☐ Sprawdzono role / duplikaty / weryfikację
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={sopChecklist.fallback_note_added} 
              onChange={() => toggleSop('fallback_note_added')}
              className="w-4 h-4 rounded accent-yellow-600"
            />
            <span className={sopChecklist.fallback_note_added ? 'text-green-700 font-medium' : ''}>
              ☐ Dodano notatkę w karcie zgłoszenia (jeśli problem)
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

// ==================== SUBMISSION DETAILS ====================

function SubmissionDetails({ submission, onChecklistUpdate, onStatusUpdate, onAddNote, onDelete, formatDate }) {
  const [noteText, setNoteText] = useState('');
  const [newStatus, setNewStatus] = useState(submission.status);
  const [showSOP, setShowSOP] = useState(false);
  
  const handleAddNote = () => {
    if (!noteText.trim()) return;
    onAddNote(submission.id, noteText.trim());
    setNoteText('');
  };
  
  return (
    <div className="space-y-6 py-4">
      {/* SOP Toggle Button */}
      <div>
        <button
          onClick={() => setShowSOP(!showSOP)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-all"
          style={{ 
            backgroundColor: showSOP ? colors.magenta : '#f8f8f8',
            color: showSOP ? 'white' : colors.black
          }}
        >
          <span className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            📋 Instrukcja dla pracownika (SOP)
          </span>
          <ChevronRight className={`w-5 h-5 transition-transform ${showSOP ? 'rotate-90' : ''}`} />
        </button>
        
        {showSOP && (
          <div className="mt-4">
            <SOPInstructions />
          </div>
        )}
      </div>
      
      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-gray-400" />
          <span>{submission.phone}</span>
        </div>
        {submission.email && (
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span>{submission.email}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <MapPinned className="w-4 h-4 text-gray-400" />
          <span>{submission.city} ({submission.location_mode === 'address' ? submission.address : submission.service_area_description})</span>
        </div>
        {submission.website && (
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            <a href={submission.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              {submission.website}
            </a>
          </div>
        )}
      </div>
      
      {/* Description */}
      <div>
        <h4 className="font-medium mb-2">Opis hodowli</h4>
        <p className="text-gray-600 bg-gray-50 rounded-lg p-3 text-sm">
          {submission.description}
        </p>
      </div>
      
      {/* Checklist */}
      <div>
        <h4 className="font-medium mb-3">Checklist</h4>
        <div className="space-y-2">
          {CHECKLIST_ITEMS.map((item) => (
            <label key={item.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={submission.checklist?.[item.id] || false}
                onChange={(e) => onChecklistUpdate(submission.id, item.id, e.target.checked)}
                className="w-5 h-5 rounded"
              />
              <span className={submission.checklist?.[item.id] ? 'text-green-700' : 'text-gray-600'}>
                {item.label}
              </span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Status Change */}
      <div>
        <h4 className="font-medium mb-3">Zmień status</h4>
        <div className="flex items-center gap-3">
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none"
          >
            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
              <option key={status} value={status}>{config.label}</option>
            ))}
          </select>
          
          <button
            onClick={() => onStatusUpdate(submission.id, newStatus)}
            disabled={newStatus === submission.status}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: colors.magenta }}
          >
            Zmień
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Uwaga: Niektóre statusy wymagają uzupełnienia checklisty.
        </p>
      </div>
      
      {/* Notes */}
      <div>
        <h4 className="font-medium mb-3">Notatki wewnętrzne</h4>
        
        {/* Add note */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Dodaj notatkę..."
            className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none"
            onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
          />
          <button
            onClick={handleAddNote}
            disabled={!noteText.trim()}
            className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
          >
            Dodaj
          </button>
        </div>
        
        {/* Notes list */}
        {submission.notes?.length > 0 ? (
          <div className="space-y-2">
            {submission.notes.map((note) => (
              <div key={note.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                <p>{note.content}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {note.author_name} · {formatDate(note.created_at)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">Brak notatek</p>
        )}
      </div>
      
      {/* Meta */}
      <div className="pt-4 border-t border-gray-100 text-sm text-gray-400">
        <p>Utworzono: {formatDate(submission.created_at)}</p>
        <p>Ostatnia aktualizacja: {formatDate(submission.updated_at)}</p>
        <p>ID: {submission.id}</p>
      </div>
      
      {/* Delete button */}
      <div className="pt-4 border-t border-gray-100">
        <button
          onClick={() => onDelete(submission.id)}
          className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Usuń zgłoszenie
        </button>
      </div>
    </div>
  );
}
